"use client";
import React, { useState } from "react";
import Icon from "@/components/icons/Icons";
import PortfolioModel from "../model/PortfolioModel";
import { useEffect } from "react";
import {
  PortfolioApiQuestion,
  PortfolioPlanCardProps,
} from "@/type/servicesPageTypes";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

type PlanKey = keyof typeof PLAN_ROUTES;

const PLAN_ROUTES = {
  "pf-personal": "personalised-portfolio",
  "mf-suggestions": "mf-suggestions",
  "pf-review": "portfolio-checkup",
  "mf-hni": "hni-mf-portfolio",
} as const;

const PlanCard: React.FC<
  PortfolioPlanCardProps & { isHighlighted?: boolean; autoOpen?: boolean }
> = ({
  subtitle,
  autoOpen,
  title,
  price,
  priceNote,
  includes,
  footerNote,
  buttonText,
  buttonUrl,
  icon,
  isDirect,
  defaultOpen,
  quesData,
  isHighlighted,
  isAssessmentSubmitted,
}) => {
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const questionData = quesData;

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (window.innerWidth < 1280 && defaultOpen) {
      setOpen(true);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (!autoOpen) return;

    if (isAssessmentSubmitted) {
      router.replace(`/portfolio-services/${buttonUrl}`);
      return;
    }

    setOpenModal(true);
    document
      .getElementById("mf-guidance")
      ?.scrollIntoView({ behavior: "smooth" });
  }, [autoOpen, isAssessmentSubmitted, buttonUrl, router]);

  const closeModal = () => {
    setOpenModal(false);
    // strip ?plan= so a refresh doesn't reopen the modal
    if (autoOpen) router.replace(pathname, { scroll: false });
  };

  return (
    <div
      className={`group max-w-96 pb-0 xs:pb-7 xl:min-w-127 overflow-hidden transition-all duration-300 shadow-[0px_10px_33px_-7px_#00000026] rounded-8xl ${isHighlighted ? "border-(--primary) bg-linear-to-b/srgb from-[rgb(var(--primary-alpha)/0.1)] to-[#FFFFFF] border-2 scale-[1.02]" : "bg-[#F6F6F6] border-2 border-[#FFFFFF] hover:border-(--primary) hover:bg-linear-to-b/srgb hover:from-[rgb(var(--primary-alpha)/0.1)] hover:to-[#FFFFFF]"}`}
    >
      <div className="p-4   xl:px-7 xl:pt-6">
        <div className="flex justify-between">
          <p className="text-xs text-[#4A5565] px-4 flex items-center justify-center h-fit py-1.5  border w-fit rounded-full">
            {subtitle}
          </p>
          {icon}
        </div>

        <div className="font-semibold text-xl xl:text-2xl mt-4 xl:mt-5">
          {title}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <p className="text-(--primary) font-bold text-2xl sm:text-3xl">
            <span className="line-through font-semibold text-1.5xl sm:text-2.5xl">
              ₹{price}
            </span>{" "}
            Free
          </p>
          <p className="pt-2 text-[#6A7282] text-sm">{priceNote}</p>
        </div>
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out  ${!isDirect ? "opacity-100 mt-4 xl:mt-5" : "max-h-0 opacity-0 mt-0"} `}
        >
          <div className="p-4 border border-[#E2E4E9] bg-white rounded-2xl">
            <p className="text-base italic text-[#5F5F5F] leading-1.3 font-medium ">
              One-time{" "}
              <span className="leading-1.3 text-black italic font-semibold">
                ₹{price}
              </span>{" "}
              Investment in a Liquid/Debt Fund.
            </p>
          </div>
        </div>
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 px-4 pb-4 xs:pb-0 xl:px-7 xl:pb-4
        xs:max-h-none xs:opacity-100
          ${open ? "max-h-screen opacity-100" : "max-h-0 opacity-0 pb-0!"}
        `}
      >
        <p className="text-xs  text-[#6A7282] tracking-wide uppercase mb-3 border-t pt-5">
          Includes
        </p>

        <div className="xl:grid xl:grid-cols-2 mb-2.5 border-b xl:min-h-100 items-stretch">
          {includes.map((item, i) => (
            <p key={i} className="text-sm text-[#4A5565] pb-2  flex flex-1 ">
              <Icon.rightSignIcon className="h-3 w-5 mt-1 text-[#009966] shrink-0" />{" "}
              {item}
            </p>
          ))}
        </div>
        <p className="flex items-center justify-center  text-[#6A7282] my-4 text-xs gap-2">
          <span>
            <Icon.limitedICon />{" "}
          </span>
          {footerNote}
        </p>

        {isAssessmentSubmitted ? (
          <Link
            href={`/portfolio-services/${buttonUrl}`}
            className={`bg-white transition-all duration-300 transform group-hover:bg-(--primary) group-hover:text-white mt-4 xl:mt-5 
            rounded-xl gap-x-1.5 items-center w-full py-3 mx-auto flex justify-center text-[#101828] border-2 border-[#B1B1B1] text-sm xl:text-base`}
          >
            <>
              {buttonText} <Icon.dropdownDownArrow className="-rotate-90" />
            </>
          </Link>
        ) : (
          <button
            onClick={() => {
              setOpenModal(true);
            }}
            className={`group-hover:bg-(--primary) transition-all duration-300 transform group-hover:text-white mt-4 xl:mt-5 
          rounded-xl gap-x-1.5 items-center w-full py-3 mx-auto flex justify-center text-[#101828] border-2 border-[#B1B1B1] text-sm xl:text-base`}
          >
            {buttonText} <Icon.dropdownDownArrow className="-rotate-90" />
          </button>
        )}
      </div>
      {openModal && (
        <PortfolioModel
          quesData={questionData || []}
          url={buttonUrl}
          onClose={closeModal}
        />
      )}

      <div className="mx-auto xs:hidden bg-[#EDF4FF] w-full ">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex justify-center py-3"
        >
          <Icon.dropdownDownArrow
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
    </div>
  );
};

export const MfGuidance: React.FC<{
  initialPlan?: PlanKey | null;
  quesData?: PortfolioApiQuestion[];
  isAssessmentSubmitted: boolean;
  debt_inv_amount: string;
  hni_inv_amount: string;
  mf_suggestions: string;
  portfolio_review: string;
}> = ({
  quesData,
  initialPlan,
  isAssessmentSubmitted,
  debt_inv_amount,
  hni_inv_amount,
  mf_suggestions,
  portfolio_review,
}) => {
  const [highlightedCard, setHighlightedCard] = useState<number | null>(null);

  useEffect(() => {
    const handleHighlight = (e: Event) => {
      const customEvent = e as CustomEvent;
      setHighlightedCard(customEvent.detail);
      setTimeout(() => setHighlightedCard(null), 3000);
    };

    window.addEventListener("highlightMfGuidanceCard", handleHighlight);
    return () =>
      window.removeEventListener("highlightMfGuidanceCard", handleHighlight);
  }, []);

  const img1 = (
    <Image
      src={"/next_assets/images/portfolio-services/chart.png"}
      alt={"charts"}
      width={44}
      height={44}
    />
  );
  const img2 = (
    <Image
      src={"/next_assets/images/portfolio-services/glass2.png"}
      alt={"glass"}
      width={44}
      height={44}
    />
  );
  const img3 = (
    <Image
      src={"/next_assets/images/portfolio-services/Page.png"}
      alt={"page"}
      width={44}
      height={44}
    />
  );
  const img4 = (
    <Image
      src={"/next_assets/images/portfolio-services/diamondguidance.png"}
      alt={"diamond"}
      width={44}
      height={44}
    />
  );

  const [active] = useState<"regular" | "direct">("regular");

  const plans: (PortfolioPlanCardProps & { planKey: PlanKey })[] = [
    {
      subtitle: "First Time Investors",
      title: "Build Your First Portfolio",
      price: debt_inv_amount,
      planKey: "pf-personal",
      priceNote: "/ Unlocked via Debt Fund investment",
      investment: debt_inv_amount,
      includes: [
        "Profiling & Goal Assessment",
        "SIP & fund selection",
        "Portfolio Tailored to Your Goals",
        "Complete Investment Support",
      ],
      footerNote: "Not suitable for portfolios above ₹10 Lakhs",
      buttonText: "Start Building My Portfolio",
      buttonUrl: PLAN_ROUTES["pf-personal"],
      icon: img1,
    },
    {
      subtitle: "New Investors",
      title: "Mutual Fund Suggestions",
      price: mf_suggestions,
      planKey: "mf-suggestions",
      priceNote: "/ via Investment in Debt Fund",
      investment: debt_inv_amount,
      includes: [
        "Category Wise Fund Shortlist",
        "Risk Match Analysis",
        "Why this Fund",
        "On boarding Assistance",
      ],
      footerNote: "Ideal if you want fund ideas without full portfolio ",
      buttonText: "Get My Fund Shortlist",
      buttonUrl: PLAN_ROUTES["mf-suggestions"],
      icon: img4,
    },
    {
      subtitle: "Existing Investors ",
      title: "Portfolio Review",
      price: portfolio_review,
      planKey: "pf-review",
      priceNote: "/ via Investment in Debt Fund",
      investment: debt_inv_amount,
      includes: [
        "CAS-based portfolio analysis",
        "Overlap & risk diagnostics",
        "Rebalancing & Switch Plan",
        "Review & Follow-Up",
      ],
      footerNote: "Not Suitable for First Time Investors",
      buttonText: "Review My Portfolio",
      buttonUrl: PLAN_ROUTES["pf-review"],
      icon: img2,
    },
    {
      subtitle: "For ₹10L+ Investments",
      title: "HNI-Level MF Guidance",
      price: hni_inv_amount,
      planKey: "mf-hni",
      priceNote: "/ via Investment in Debt Fund",
      investment: hni_inv_amount,
      includes: [
        "Private Investment Advisory",
        "Core–Satellite portfolio strategy",
        "Wealth Planning Support",
        "Priority & Execution Support",
      ],
      footerNote: "Limited HNI Clients Slots",
      buttonText: "Request a Private Strategy Session",
      buttonUrl: PLAN_ROUTES["mf-hni"],
      icon: img3,
    },
  ];

  return (
    <>
      <section
        id="mf-guidance"
        className="mt-14 xl:mt-32 w-full mb-16 lg:mb-36 px-4"
      >
        <h2 className="font-bold text-xl sm:text-2xl lg:text-3xl text-primaryBlack text-center mb-2">
          Choose the Right Service for Your Portfolio Stage
        </h2>
        <p className="text-center text-sm lg:text-base text-[#404040] mb-4 xl:mb-8">
          Each Service Begins with an Investment to Ensure Alignment and Intent
        </p>
        <div className="flex flex-col justify-center items-center xs:flex-row xs:flex-wrap xl:grid xl:grid-cols-2 xl:w-fit xl:mx-auto gap-5 md:gap-10">
          {plans.map((plan, i) => (
            <PlanCard
              key={i}
              {...plan}
              quesData={quesData ?? []}
              isDirect={active === "direct"}
              defaultOpen={i === 0}
              autoOpen={initialPlan === plan.planKey}
              isHighlighted={highlightedCard === i}
              isAssessmentSubmitted={isAssessmentSubmitted}
            />
          ))}
        </div>
      </section>
    </>
  );
};
